import React, { useCallback, useEffect, useState } from "react";
import { cn } from "@/shared/utils/shadcn";
import { Row } from "@/shared/ui/Blocks";
import { RichTextEditor } from "@/shared/widgets/RichTextEditor";
import { WisgptContextTokenQueryVariables } from "@/shared/services/api/graphql/generated";
import { IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { Title } from "@/shared/ui/Title";
import MarkdownText from "@/shared/widgets/MarkdownText";
import { CustomTagsData } from "@/shared/widgets/MarkdownText/_components/MarkdownComponents";
import { useSummaryGeneration } from "@/features/assessments/hooks/useSummaryGeneration";
import { useTranslation } from "react-i18next";

interface SummaryToolSectionProps
  extends Omit<React.HTMLProps<HTMLDivElement>, "scope"> {
  onDeleteSummary: () => void;
  onSaveSummary: (summary: string) => void;
  loading: boolean;
  deleting: boolean;
  summaryContent?: string | null;
  isDisabled?: boolean;
  updatedAt?: string | null;
  contextLabel?: string;
  flowId: string;
  scope: WisgptContextTokenQueryVariables;
  title?: string;
  subtitle?: string;
  titleIcon?: IconName;
  tagsData?: CustomTagsData;
  hideGenerateButton?: boolean;
  titleAs?: "h3" | "h4" | "h5" | "h6";
}

function SummaryToolSection(props: SummaryToolSectionProps) {
  const {
    onDeleteSummary,
    onSaveSummary,
    summaryContent,
    updatedAt,
    isDisabled,
    className,
    loading,
    deleting,
    flowId,
    scope,
    title,
    titleIcon,
    subtitle,
    tagsData,
    hideGenerateButton = false,
    titleAs = "h3",
    ...rest
  } = props;

  const [showEditor, setShowEditor] = useState(false);
  const [localGenerating, setLocalGenerating] = useState(false);
  const { t } = useTranslation();
  const { generateSummary, summaryBuffer, isGenerating, setSummaryBuffer } =
    useSummaryGeneration({
      flowId,
      scope,
      onSaveSummary,
      onStartGeneration: () => {
        setShowEditor(false);
        setLocalGenerating(true);
      },
      onEndGeneration: () => setLocalGenerating(false),
    });

  useEffect(() => {
    setSummaryBuffer(summaryContent || "");
  }, [summaryContent, setSummaryBuffer]);

  const handleDeleteSummary = useCallback(() => {
    setSummaryBuffer("");
    setShowEditor(false);
    onDeleteSummary();
  }, [onDeleteSummary, setSummaryBuffer]);

  const handleClickSummaryTool = useCallback(() => {
    setShowEditor((prev) => !prev);
  }, []);

  const handleSaveSummary = useCallback(() => {
    setShowEditor(false);
    if (summaryContent === summaryBuffer) {
      return;
    }
    onSaveSummary(summaryBuffer);
  }, [onSaveSummary, summaryBuffer, summaryContent]);

  const handleGenerateSummary = useCallback(() => {
    generateSummary();
  }, [generateSummary]);

  function TitleButtons() {
    if (hideGenerateButton) {
      return (
        <Row className="gap-4">
          {!showEditor && (
            <IconButton
              variant="outline"
              size="md"
              color="darkGrey"
              icon="EditorTool"
              label={t("common.summary_editor")}
              disabled={isGenerating || localGenerating || loading}
              onClick={handleClickSummaryTool}
            />
          )}
          {showEditor && (
            <IconButton
              size="md"
              color="white"
              icon="Check"
              label="Done"
              onClick={handleSaveSummary}
            />
          )}
        </Row>
      );
    }

    return (
      <Row className="gap-4">
        {!showEditor && (
          <IconButton
            variant="outline"
            size="md"
            color="darkGrey"
            icon="EditorTool"
            label={t("common.summary_editor")}
            disabled={isGenerating || localGenerating || loading}
            onClick={handleClickSummaryTool}
          />
        )}
        <IconButton
          variant="outline"
          color="purple"
          size="md"
          icon={summaryContent ? "Restart" : "Magicwand"}
          label={summaryContent ? "Regenerate summary" : "Generate summary"}
          className="bg-purple-100 text-purple-500 hover:bg-purple-100 hover:border-purple-500"
          onClick={handleGenerateSummary}
          disabled={isGenerating || localGenerating || deleting}
          loading={isGenerating || localGenerating}
        />
        {showEditor && (
          <IconButton
            size="md"
            color="white"
            icon="Check"
            label="Done"
            onClick={handleSaveSummary}
          />
        )}
      </Row>
    );
  }

  return (
    <section className={cn("flex flex-col", className)} {...rest}>
      <Row className="items-center justify-between border-b border-b-solid border-grey-300 pb-4 mb-4">
        <Title
          as={titleAs}
          icon={titleIcon}
          description={subtitle}
          color="blue"
        >
          {title}
        </Title>
        <TitleButtons />
      </Row>
      {showEditor && !isGenerating && !localGenerating && (
        <RichTextEditor
          placeholder="Your Executive Summary goes here..."
          defaultValue={summaryContent || ""}
          onChange={(value) => setSummaryBuffer(value)}
          alwaysShowToolbar
          tagsData={tagsData}
          ToolbarRightComponent={
            <Row className="gap-4">
              {summaryContent && (
                <IconButton
                  color="red"
                  variant="ghost"
                  icon="Delete"
                  disabled={!summaryContent}
                  onClick={handleDeleteSummary}
                  loading={deleting}
                />
              )}
            </Row>
          }
        />
      )}
      {!showEditor && <MarkdownText data={tagsData} text={summaryBuffer} />}
      {/* <div
        ref={summaryRef}
        className={cn("relative w-full", !!showEditor && "hidden")}
      >
        <div
          className="relative [&_p]:mb-4 [&_p]:leading-8 prose max-w-none"
          dangerouslySetInnerHTML={getContentHtml(
            markdownToHtml(summaryBuffer),
            false
          )}
        />
        {dimensionTagPortals}
      </div> */}
    </section>
  );
}

export { SummaryToolSection };
